/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, July 7th 2024, 10:34:24 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { APISERVICEPATHS, useAPIForm, UseAPIFormSpec, useAPIRequest } from "lib/api";
import { ContentTag, ContentTagValidationSchema } from "lib/schemas";
import { useDeleteOSItem } from "version-control/services";

const contentTagsAPIURL = APISERVICEPATHS.contentTags;

interface UseContentTagsAPIFormSpec {
  contentTagId: ContentTag["id"];
  newContentTagDefault: ContentTag;
  updateContentTagFromAPICallback: UseAPIFormSpec<ContentTag>["setAPIFormResponse"];
}

const useContentTagsAPIForm = ({
  contentTagId,
  newContentTagDefault,
  updateContentTagFromAPICallback,
}: UseContentTagsAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm({
    apiServicePath: contentTagsAPIURL,
    apiFormDataValidationSchema: ContentTagValidationSchema,
    apiFormDefaultValues: newContentTagDefault,
    apiInitialFetchId: contentTagId,
    primaryKey: "id",
    setAPIFormResponse: updateContentTagFromAPICallback,
  });

  return useAPIFormReturn;
};

const useFetchContentTagsAPI = () => {
  const { makeRequest, ...rest } = useAPIRequest<ContentTag[]>({
    apiServicePath: contentTagsAPIURL,
    makeRequestOnInitialMount: false,
  });

  const fetchContentTags = React.useCallback(
    (onFetchContentTags: Parameters<typeof makeRequest>[0]["setValidAPIResponse"]) => {
      makeRequest({ setValidAPIResponse: onFetchContentTags });
    },
    [makeRequest]
  );

  return { fetchContentTags, ...rest };
};

const useDeleteContentTag = () => useDeleteOSItem(contentTagsAPIURL);

export const useContentTagsAPI = () => ({
  useContentTagsAPIForm,
  useDeleteContentTag,
  useFetchContentTagsAPI,
});
